import React, { createContext, isValidElement, ReactElement, ReactNode, useMemo, useState } from 'react';
import { isNull, map, omitBy, uniqueId } from 'lodash';

export type ModalContextProps = {
  isModalOpen: boolean;
  closeModal: () => void;
};

export const ModalContext = createContext<ModalContextProps>({
  isModalOpen: false,
  closeModal: () => {
    return;
  },
});

export type ModalOpenerContextProps = {
  openModal: (modal: ReactElement) => void;
};

export const ModalOpenerContext = createContext<ModalOpenerContextProps>({
  openModal: () => {
    return;
  },
});

type OpenedModals = {
  [modalId: string]: {
    modalComponent: ReactElement;
    unmountAfter?: number;
    modalContext: ModalContextProps;
  };
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [openedModals, setOpenedModals] = useState<OpenedModals>({});

  const openerContext = useMemo(
    () => ({
      openModal: (modal: ReactElement) => {
        if (isNull(modal) || !isValidElement(modal)) {
          throw new Error(`Modal could not be be opened. Invalid modal: ${modal}`);
        } else {
          const modalId = uniqueId();

          const closeModal = () => {
            setOpenedModals(modals => {
              return {
                ...modals,
                [modalId]: {
                  modalComponent: modals[modalId].modalComponent,
                  unmountAfter: new Date().getTime() + 1000, // mark for being ok to unmount soon
                  modalContext: {
                    isModalOpen: false,
                    closeModal: () => {
                      return;
                    },
                  },
                },
              };
            });
          };

          setOpenedModals(modals => {
            // unmount old modals whenever opening a new one, just for performance
            const modalsToKeepMounted = omitBy(modals, ({ unmountAfter }) => {
              return unmountAfter !== undefined && unmountAfter < new Date().getTime();
            });
            return {
              ...modalsToKeepMounted,
              [modalId]: {
                modalComponent: modal,
                modalContext: {
                  isModalOpen: true,
                  closeModal,
                },
              },
            };
          });
        }
      },
    }),
    [],
  );

  return (
    <ModalOpenerContext.Provider value={openerContext}>
      {map(openedModals, ({ modalContext, modalComponent }, modalId) => (
        <ModalContext.Provider key={modalId} value={modalContext}>
          {modalComponent}
        </ModalContext.Provider>
      ))}
      {children}
    </ModalOpenerContext.Provider>
  );
};
